import React from 'react';
import { categoriaDeTipo, type CategoriaError } from './metricas';
import type { Accion, ErrorAnalisis, Fidelidad, MotivoEdicion, MotivoEdicionCaso, StatusRun } from './tipos';

// Piezas visuales chicas compartidas por las tres sub-vistas de Análisis. Sin
// lógica: los colores y formatos de acá tienen que verse IGUALES en Casos,
// Corridas y Comparar para que el ojo compare sin recalibrar.

export const CHIP = 'inline-block rounded-full px-2 py-0.5 text-[11px] leading-4';

/** Color por categoría del catálogo (prefijo del tipo). Producto en rojo: es lo que más pesa en la decisión de regenerar. */
export const COLOR_CATEGORIA: Record<CategoriaError, string> = {
  producto: 'bg-red-100 text-red-700',
  modelo: 'bg-purple-100 text-purple-700',
  escena: 'bg-blue-100 text-blue-700',
  texto: 'bg-fuchsia-100 text-fuchsia-700',
  tecnica: 'bg-gray-200 text-gray-700',
  instruccion: 'bg-emerald-100 text-emerald-700',
  /** Problemas de las fotos de producto cargadas (→ pedir fotos). */
  carga: 'bg-amber-100 text-amber-700',
  otro: 'bg-gray-100 text-gray-500',
};

/** Máximo verde, 3 lima, 2 naranja, 1 rojo. Un 5 viejo (escala 1–5) cuenta como máximo. */
export function colorFidelidad(f: Fidelidad | null, fidelidadMax: number): string {
  if (f === null) return 'text-gray-400';
  if (f >= fidelidadMax) return 'text-green-600';
  if (f >= 3) return 'text-lime-600';
  if (f === 2) return 'text-orange-500';
  return 'text-red-600';
}

export const LABEL_ACCION: Record<Accion, string> = {
  aceptar: 'OK',
  regenerar: 'REGENERAR',
  pedir_fotos: 'PEDIR FOTOS',
};

const COLOR_ACCION: Record<Accion, string> = {
  aceptar: 'bg-green-600 text-white',
  regenerar: 'bg-red-600 text-white',
  pedir_fotos: 'bg-amber-500 text-white',
};

/** Badge de la decisión del backend; null = sin decisión (análisis fallido). */
export const ChipAccion: React.FC<{ accion: Accion | null; className?: string }> = ({ accion, className = '' }) => (
  <span className={`${CHIP} font-semibold tracking-wide ${accion ? COLOR_ACCION[accion] : 'bg-gray-200 text-gray-600'} ${className}`}>
    {accion ? LABEL_ACCION[accion] : 'SIN DECISIÓN'}
  </span>
);

/**
 * Chip de un error detectado: SOLO el tipo, con color por categoría. La severidad que
 * trae el backend no se muestra en ningún lado de esta pestaña (la decisión es por
 * puntaje general + tipo). La descripción va a la vista al lado del chip
 * (ResultadoResumen); el `title` es un extra, nada depende del hover.
 */
export const ChipError: React.FC<{ error: ErrorAnalisis }> = ({ error }) => (
  <span title={error.tipo} className={`${CHIP} shrink-0 whitespace-nowrap ${COLOR_CATEGORIA[categoriaDeTipo(error.tipo)]}`}>
    {error.tipo}
  </span>
);

const COLOR_STATUS: Record<NonNullable<StatusRun> | 'sin', string> = {
  queued: 'bg-gray-200 text-gray-600',
  running: 'bg-blue-100 text-blue-700 animate-pulse',
  succeeded: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  partial: 'bg-amber-100 text-amber-700',
  sin: 'bg-gray-100 text-gray-500',
};

export const ChipStatus: React.FC<{ status: StatusRun }> = ({ status }) => (
  <span className={`${CHIP} ${COLOR_STATUS[status ?? 'sin']}`}>{status ?? 'sin estado'}</span>
);

export const ChipTipoCaso: React.FC<{ tipo: 'editada' | 'control' }> = ({ tipo }) => (
  <span className={`${CHIP} ${tipo === 'editada' ? 'bg-orange-100 text-orange-700' : 'bg-sky-100 text-sky-700'}`}>
    {tipo}
  </span>
);

export const LABEL_MOTIVO: Record<MotivoEdicion, string> = {
  fallo_generacion: 'FALLO',
  ambiguo: 'AMBIGUO',
  cambio_creativo: 'CREATIVO',
};

/** Fallo en rojo (es lo que se busca para el set), ambiguo en ámbar (hay que mirarlo), creativo en gris (descarte). */
const COLOR_MOTIVO: Record<MotivoEdicion, string> = {
  fallo_generacion: 'bg-red-600 text-white',
  ambiguo: 'bg-amber-500 text-white',
  cambio_creativo: 'bg-gray-200 text-gray-700',
};

/** Badge del motivo de edición clasificado por IA, con la confianza al lado ("FALLO · alta"). */
export const ChipMotivo: React.FC<{ motivo: MotivoEdicionCaso; className?: string }> = ({ motivo, className = '' }) => (
  <span
    className={`${CHIP} font-semibold tracking-wide ${COLOR_MOTIVO[motivo.motivo]} ${className}`}
    title={`Motivo de la edición según la IA (confianza ${motivo.confianza})`}
  >
    {LABEL_MOTIVO[motivo.motivo]}
    <span className="font-normal tracking-normal opacity-80"> · {motivo.confianza}</span>
  </span>
);

/** "03/09 21:58" — fecha corta para listas; el año no importa en un harness. */
export function formatearFecha(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/** Aviso de error SIEMPRE visible (nunca un catch silencioso). */
export const Aviso: React.FC<{ error: string | null; className?: string }> = ({ error, className = '' }) =>
  error ? (
    <p className={`rounded-lg border border-red-200 bg-red-50 py-2 px-4 text-sm text-red-700 ${className}`}>{error}</p>
  ) : null;

export const btnPrimario = 'rounded-lg bg-brand-black py-2 px-4 text-sm font-medium text-white disabled:opacity-50';
export const btnSecundario =
  'rounded-lg border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-brand-black hover:border-gray-500 disabled:opacity-50';
export const inputCls = 'rounded-lg border border-gray-300 bg-white py-2 px-4 text-sm text-brand-black';
export const inputChicoCls = 'rounded-md border border-gray-300 bg-white py-1.5 px-2 text-sm text-brand-black';
